import React, { useState, useEffect } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AdminPanelSettings,
  Apps,
  Badge as BadgeIcon,
  Build as BuildIcon,
  AccountBalance,
  CalendarMonth,
  Receipt,
  ChevronRight,
  ContactSupport,
  DateRange,
  EventNote,
  DriveFileRenameOutline,
  ExpandMore,
  FolderOpen,
  Groups,
  Help,
  Info as InfoIcon,
  Gavel,
  LocalOffer,
  ManageAccounts,
  MenuBook,
  PeopleAlt,
  Policy,
  Schedule,
  Tune as TuneIcon,
  Functions as FunctionsIcon,
  SettingsApplications as GlobalSettingsIcon,
  Settings as SettingsIcon,
  WorkspacesOutlined,
  AccessTime,
  VpnKey,
  CloudUpload,
  FactCheck as FactCheckIcon,
  Assessment,
  TableChart,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const drawerWidth = 260;
const miniDrawerWidth = 72;

type SectionKey = 'Scheduling' | 'Leave Management' | 'Employee Entry' | 'Identity Manager' | 'Application' | 'Timekeeping' | 'Report' | 'Maintenance' | 'Support';

const sectionPaths: Record<SectionKey, string[]> = {
  Scheduling: ['/shifts', '/shift-groups', '/shift-distributions'],
  'Leave Management': ['/leave-types', '/leave-policies', '/leave-balance', '/leave-transactions', '/public-holidays'],
  'Employee Entry': ['/employees', '/employee-tags', '/system/custom-fields', '/system/employee-entry-settings', '/system/calculated-values'],
  'Identity Manager': ['/system/roles', '/system/users', '/system/user-field-access', '/timekeeping/api-keys'],
  Application: ['/system/global-settings', '/storage'],
  Timekeeping: ['/timekeeping/import', '/timekeeping/parser-profiles', '/timekeeping/manual-entry', '/timekeeping/error-correction', '/timekeeping/data-autopsy'],
  Report: ['/reports/data-source'],
  Maintenance: ['/maintenance/system-states', '/maintenance/import'],
  Support: ['/help', '/docs', '/system-info'],
};

function getSectionForPath(pathname: string): SectionKey | null {
  for (const [section, paths] of Object.entries(sectionPaths)) {
    if (paths.includes(pathname)) return section as SectionKey;
  }
  return null;
}

interface SideNavProps {
  open: boolean;
}

const SideNav: React.FC<SideNavProps> = ({ open }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { permissions } = useAuth();

  const [expandedSection, setExpandedSection] = useState<SectionKey | null>(
    () => getSectionForPath(location.pathname),
  );

  useEffect(() => {
    const section = getSectionForPath(location.pathname);
    if (section) setExpandedSection(section);
  }, [location.pathname]);

  const isSelected = (path: string) => location.pathname === path;
  const hasPermission = (perm: string) => permissions.includes(perm);

  const toggleSection = (section: SectionKey) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const isSectionExpanded = (section: SectionKey) =>
    !open || expandedSection === section;

  const SectionHeader = ({
    title,
    icon,
    sectionKey,
  }: {
    title: string;
    icon: React.ReactNode;
    sectionKey: SectionKey;
  }) => {
    if (!open) return null;
    const isExpanded = expandedSection === sectionKey;
    return (
      <ListItem disablePadding sx={{ display: 'block', mb: 0.25 }}>
        <ListItemButton
          onClick={() => toggleSection(sectionKey)}
          sx={{
            minHeight: 40,
            px: 2,
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 1.5, color: 'primary.main' }}>
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={title}
            sx={{
              '& .MuiTypography-root': {
                fontWeight: 700,
                fontSize: '0.875rem',
                color: 'text.primary',
              },
            }}
          />
          <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </Box>
        </ListItemButton>
      </ListItem>
    );
  };

  const SubGroupLabel = ({ title }: { title: string }) => {
    if (!open) return null;
    return (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          pl: 5,
          pr: 2,
          pt: 0.75,
          pb: 0.25,
          color: 'text.secondary',
          fontSize: '0.72rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {title}
      </Typography>
    );
  };

  const NavItem = ({
    text,
    icon,
    path,
    depth = 1,
  }: {
    text: string;
    icon: React.ReactNode;
    path: string;
    depth?: number;
  }) => (
    <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
      <Tooltip title={!open ? text : ''} placement="right">
        <ListItemButton
          onClick={() => navigate(path)}
          selected={isSelected(path)}
          sx={{
            minHeight: 40,
            justifyContent: open ? 'initial' : 'center',
            pl: open ? 2 + depth * 1.5 : 2,
            pr: 2,
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              color: 'primary.main',
              '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' },
            },
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 1.5 : 'auto',
              justifyContent: 'center',
              color: isSelected(path) ? 'primary.main' : 'text.secondary',
              '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={text}
            sx={{
              opacity: open ? 1 : 0,
              '& .MuiTypography-root': {
                fontWeight: isSelected(path) ? 600 : 400,
                fontSize: '0.875rem',
              },
            }}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  const SectionDivider = () => <Divider sx={{ my: 1, mx: 2 }} />;

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : miniDrawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : miniDrawerWidth,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          overflowY: 'auto',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          border: 'none',
          borderRight: '1px solid',
          borderColor: 'divider',
          '&::-webkit-scrollbar': { width: open ? 6 : 0 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: 3 },
          '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
        },
      }}
    >
      <Toolbar />
      <Box sx={{ overflowX: 'hidden', mt: 1.5 }}>

        {/* Employee Entry */}
        {(hasPermission('VIEW_EMPLOYEE_PAGE') || hasPermission('VIEW_APPLICATION_SETTING_PAGE') || hasPermission('VIEW_EMPLOYEE_TAG_PAGE') || hasPermission('VIEW_CUSTOM_FIELD_PAGE')) && (
          <List sx={{ px: 1.5, py: 0 }}>
            <SectionHeader title="Employee Entry" icon={<Groups />} sectionKey="Employee Entry" />
            <Collapse in={isSectionExpanded('Employee Entry')} timeout="auto" unmountOnExit>
              {hasPermission('VIEW_EMPLOYEE_PAGE') && (
                <NavItem text="Employees" icon={<BadgeIcon />} path="/employees" />
              )}
              {(hasPermission('VIEW_EMPLOYEE_TAG_PAGE') || hasPermission('VIEW_CUSTOM_FIELD_PAGE') || hasPermission('VIEW_APPLICATION_SETTING_PAGE')) && (
                <>
                  <SubGroupLabel title="Customization" />
                  {hasPermission('VIEW_EMPLOYEE_TAG_PAGE') && (
                    <NavItem text="Employee Tags" icon={<LocalOffer />} path="/employee-tags" depth={2} />
                  )}
                  {hasPermission('VIEW_CUSTOM_FIELD_PAGE') && (
                    <NavItem text="Custom Fields" icon={<DriveFileRenameOutline />} path="/system/custom-fields" depth={2} />
                  )}
                  {hasPermission('VIEW_APPLICATION_SETTING_PAGE') && (
                    <NavItem text="Entry Settings" icon={<TuneIcon />} path="/system/employee-entry-settings" depth={2} />
                  )}
                  {hasPermission('VIEW_APPLICATION_SETTING_PAGE') && (
                    <NavItem text="Calculated Values" icon={<FunctionsIcon />} path="/system/calculated-values" depth={2} />
                  )}
                </>
              )}
            </Collapse>
          </List>
        )}

        {/* Scheduling */}
        {hasPermission('VIEW_SHIFT_PAGE') && (
          <>
            <SectionDivider />
            <List sx={{ px: 1.5, py: 0 }}>
              <SectionHeader title="Scheduling" icon={<DateRange />} sectionKey="Scheduling" />
              <Collapse in={isSectionExpanded('Scheduling')} timeout="auto" unmountOnExit>
                <NavItem text="Shifts" icon={<Schedule />} path="/shifts" />
                {hasPermission('VIEW_SHIFT_GROUP_PAGE') && (
                  <NavItem text="Shift Groups" icon={<WorkspacesOutlined />} path="/shift-groups" />
                )}
                {hasPermission('VIEW_SHIFT_DISTRIBUTION_PAGE') && (
                  <NavItem text="Shift Distributions" icon={<CalendarMonth />} path="/shift-distributions" />
                )}
              </Collapse>
            </List>
          </>
        )}

        {/* Leave Management */}
        {hasPermission('VIEW_LEAVE_NAVIGATION_MENU') && (
          <>
            <SectionDivider />
            <List sx={{ px: 1.5, py: 0 }}>
              <SectionHeader title="Leave Management" icon={<EventNote />} sectionKey="Leave Management" />
              <Collapse in={isSectionExpanded('Leave Management')} timeout="auto" unmountOnExit>
                <NavItem text="Leave Types" icon={<Gavel />} path="/leave-types" />
                <NavItem text="Leave Policies" icon={<Policy />} path="/leave-policies" />
                <NavItem text="Leave Balance" icon={<AccountBalance />} path="/leave-balance" />
                <NavItem text="Transactions" icon={<Receipt />} path="/leave-transactions" />
                <NavItem text="Public Holidays" icon={<CalendarMonth />} path="/public-holidays" />
              </Collapse>
            </List>
          </>
        )}

        {/* Timekeeping */}
        {(hasPermission('VIEW_TIMEKEEPING_PAGE') || hasPermission('VIEW_TIMEKEEPING_ERROR_PAGE')) && (
          <>
            <SectionDivider />
            <List sx={{ px: 1.5, py: 0 }}>
              <SectionHeader title="Timekeeping" icon={<AccessTime />} sectionKey="Timekeeping" />
              <Collapse in={isSectionExpanded('Timekeeping')} timeout="auto" unmountOnExit>
                <NavItem text="Import" icon={<EventNote />} path="/timekeeping/import" />
                <NavItem text="Parser Profiles" icon={<SettingsIcon />} path="/timekeeping/parser-profiles" />
                <NavItem text="Manual Entry" icon={<EventNote />} path="/timekeeping/manual-entry" />
                {hasPermission('VIEW_TIMEKEEPING_ERROR_PAGE') && (
                  <NavItem text="Error Correction" icon={<EventNote />} path="/timekeeping/error-correction" />
                )}
                {hasPermission('VIEW_TIMEKEEPING_ERROR_PAGE') && (
                  <NavItem text="Data Autopsy" icon={<FactCheckIcon />} path="/timekeeping/data-autopsy" />
                )}
              </Collapse>
            </List>
          </>
        )}

        {/* Report */}
        {hasPermission('VIEW_EMPLOYEE_PAGE') && (
          <>
            <SectionDivider />
            <List sx={{ px: 1.5, py: 0 }}>
              <SectionHeader title="Report" icon={<Assessment />} sectionKey="Report" />
              <Collapse in={isSectionExpanded('Report')} timeout="auto" unmountOnExit>
                <NavItem text="Data Source" icon={<TableChart />} path="/reports/data-source" />
              </Collapse>
            </List>
          </>
        )}

        {/* Identity Manager */}
        {(hasPermission('VIEW_ROLE_NAVIGATION_MENU') || hasPermission('VIEW_USER_NAVIGATION_MENU') || hasPermission('VIEW_USER_FIELD_DENIAL_NAVIGATION_MENU') || hasPermission('VIEW_API_KEY_NAVIGATION_MENU')) && (
          <>
            <SectionDivider />
            <List sx={{ px: 1.5, py: 0 }}>
              <SectionHeader title="Identity Manager" icon={<AdminPanelSettings />} sectionKey="Identity Manager" />
              <Collapse in={isSectionExpanded('Identity Manager')} timeout="auto" unmountOnExit>
                {hasPermission('VIEW_ROLE_NAVIGATION_MENU') && (
                  <NavItem text="Role Management" icon={<ManageAccounts />} path="/system/roles" />
                )}
                {hasPermission('VIEW_USER_NAVIGATION_MENU') && (
                  <NavItem text="User Management" icon={<PeopleAlt />} path="/system/users" />
                )}
                {hasPermission('VIEW_USER_FIELD_DENIAL_NAVIGATION_MENU') && (
                  <NavItem text="Field Access" icon={<Policy />} path="/system/user-field-access" />
                )}
                {hasPermission('VIEW_API_KEY_NAVIGATION_MENU') && (
                  <NavItem text="API Keys" icon={<VpnKey />} path="/timekeeping/api-keys" />
                )}
              </Collapse>
            </List>
          </>
        )}

        {/* Application */}
        {(hasPermission('VIEW_APPLICATION_SETTING_PAGE') || hasPermission('VIEW_STORAGE_NAVIGATION_MENU')) && (
          <>
            <SectionDivider />
            <List sx={{ px: 1.5, py: 0 }}>
              <SectionHeader title="Application" icon={<Apps />} sectionKey="Application" />
              <Collapse in={isSectionExpanded('Application')} timeout="auto" unmountOnExit>
                {hasPermission('VIEW_APPLICATION_SETTING_PAGE') && (
                  <NavItem text="Global Settings" icon={<GlobalSettingsIcon />} path="/system/global-settings" />
                )}
                {hasPermission('VIEW_STORAGE_NAVIGATION_MENU') && (
                  <NavItem text="Storage" icon={<FolderOpen />} path="/storage" />
                )}
              </Collapse>
            </List>
          </>
        )}

        {/* Maintenance */}
        {hasPermission('VIEW_MAINTENANCE_NAVIGATION_MENU') && (
          <>
            <SectionDivider />
            <List sx={{ px: 1.5, py: 0 }}>
              <SectionHeader title="Maintenance" icon={<BuildIcon />} sectionKey="Maintenance" />
              <Collapse in={isSectionExpanded('Maintenance')} timeout="auto" unmountOnExit>
                <NavItem text="System States" icon={<InfoIcon />} path="/maintenance/system-states" />
                <NavItem text="Import" icon={<CloudUpload />} path="/maintenance/import" />
              </Collapse>
            </List>
          </>
        )}

        {/* Support */}
        <SectionDivider />
        <List sx={{ px: 1.5, py: 0 }}>
          <SectionHeader title="Support" icon={<ContactSupport />} sectionKey="Support" />
          <Collapse in={isSectionExpanded('Support')} timeout="auto" unmountOnExit>
            <NavItem text="Help" icon={<Help />} path="/help" />
            <NavItem text="Documentation" icon={<MenuBook />} path="/docs" />
            <NavItem text="System Info" icon={<InfoIcon />} path="/system-info" />
          </Collapse>
        </List>

      </Box>
    </Drawer>
  );
};

export default SideNav;
